// Create / edit dialog for a training class (see `features/training-classes.md`).
// A class carries a name, a structured weekly schedule (a list of weekday +
// start/end time slots) and a free-text description; the owning company is
// fixed at creation. Mutations respond with the company's refreshed
// { classes, coaches, enrollments } snapshot, handed to `onSaved`.

import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { TrainingAudience, TrainingClass, TrainingSlot, Weekday } from '../domain';
import {
  TRAINING_AUDIENCES,
  TRAINING_CLASS_DESCRIPTION_MAX,
  TRAINING_CLASS_NAME_MAX,
  TRAINING_SLOT_MAX,
  WEEKDAYS,
  groupSchedulePatterns,
  schedulePatternLabel,
  validateTrainingClass,
} from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import { createTrainingClass, updateTrainingClass } from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

interface TrainingClassDialogProps {
  open: boolean;
  /** The company a class created here belongs to. */
  companyId: number;
  /** The class being edited, or null to create a new one. */
  trainingClass: TrainingClass | null;
  onClose: () => void;
  onSaved: (snapshot: TrainingClassesSnapshot) => void;
}

const DEFAULT_SLOT: TrainingSlot = { day: 'MON', startTime: '18:00', endTime: '19:30' };

export function TrainingClassDialog({
  open,
  companyId,
  trainingClass,
  onClose,
  onSaved,
}: TrainingClassDialogProps) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [slots, setSlots] = useState<TrainingSlot[]>([]);
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState<TrainingAudience>('ALL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // (Re)seed the form whenever the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    setName(trainingClass?.name ?? '');
    setSlots(trainingClass?.schedule ?? []);
    setDescription(trainingClass?.description ?? '');
    setAudience(trainingClass?.audience ?? 'ALL');
    setError(null);
  }, [open, trainingClass]);

  const validation = useMemo(
    () => validateTrainingClass({ name, schedule: slots, description, audience }),
    [name, slots, description, audience],
  );

  const updateSlot = (index: number, patch: Partial<TrainingSlot>) => {
    setSlots((prev) => prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));
  };
  const addSlot = () => setSlots((prev) => [...prev, { ...DEFAULT_SLOT }]);
  const removeSlot = (index: number) =>
    setSlots((prev) => prev.filter((_, i) => i !== index));

  const submit = async () => {
    if (!validation.valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Send the canonically-ordered, validated slots rather than raw state.
      const input = {
        name,
        schedule: validation.value?.schedule ?? [],
        description,
        audience,
      };
      const snapshot = trainingClass
        ? await updateTrainingClass(trainingClass.id, input)
        : await createTrainingClass(companyId, input);
      onSaved(snapshot);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.trainingClasses.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {trainingClass
          ? t('admin.trainingClasses.dialog.editTitle', { name: trainingClass.name })
          : t('admin.trainingClasses.dialog.createTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('admin.trainingClasses.dialog.name')}
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: TRAINING_CLASS_NAME_MAX } }}
          />

          {/* Weekly schedule editor */}
          <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {t('admin.trainingClasses.dialog.schedule')}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={slots.length >= TRAINING_SLOT_MAX}
                onClick={addSlot}
              >
                {t('admin.trainingClasses.dialog.addSlot')}
              </Button>
            </Stack>

            {slots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('admin.trainingClasses.dialog.noSlots')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {slots.map((slot, index) => {
                  const invalid = slot.endTime <= slot.startTime;
                  return (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <TextField
                        select
                        size="small"
                        label={t('admin.trainingClasses.dialog.slotDay')}
                        value={slot.day}
                        onChange={(e) =>
                          updateSlot(index, { day: e.target.value as Weekday })
                        }
                        sx={{ minWidth: 110 }}
                      >
                        {WEEKDAYS.map((day) => (
                          <MenuItem key={day} value={day}>
                            {t(`admin.trainingClasses.weekdays.${day}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        type="time"
                        size="small"
                        label={t('admin.trainingClasses.dialog.slotFrom')}
                        value={slot.startTime}
                        onChange={(e) => updateSlot(index, { startTime: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <TextField
                        type="time"
                        size="small"
                        label={t('admin.trainingClasses.dialog.slotTo')}
                        value={slot.endTime}
                        error={invalid}
                        onChange={(e) => updateSlot(index, { endTime: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <Tooltip title={t('admin.trainingClasses.dialog.removeSlot')}>
                        <IconButton
                          aria-label={t('admin.trainingClasses.dialog.removeSlot')}
                          onClick={() => removeSlot(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                })}
              </Stack>
            )}
            {validation.errors.schedule && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {validation.errors.schedule}
              </Typography>
            )}
            {/* Live "Meets Tue, Thu · 16:00–17:30" preview of the weekly
                rhythm being edited — the same lines the group card shows. */}
            {validation.valid && (validation.value?.schedule.length ?? 0) > 0 && (
              <Box sx={{ mt: 1 }}>
                {groupSchedulePatterns(validation.value?.schedule ?? []).map((pattern, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {t('admin.trainingClasses.meets', {
                      pattern: schedulePatternLabel(pattern, (day) =>
                        t(`admin.trainingClasses.weekdays.${day}`),
                      ),
                    })}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          <TextField
            select
            label={t('admin.trainingClasses.dialog.audience')}
            helperText={t('admin.trainingClasses.dialog.audienceHelp')}
            value={audience}
            onChange={(e) => setAudience(e.target.value as TrainingAudience)}
          >
            {TRAINING_AUDIENCES.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`admin.trainingClasses.audience.${option}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('admin.trainingClasses.dialog.description')}
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            slotProps={{ htmlInput: { maxLength: TRAINING_CLASS_DESCRIPTION_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !validation.valid}
          onClick={() => void submit()}
        >
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
